import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Prompt } from "react-router";
import { Button, Space } from "antd";
import { filter, find, head, isNil, map } from "lodash";
import { LEAVE_CONFIRMATION } from "constants/common";
import { aiccSttSettingApi } from "services/aiccLearningAssets";
import {
  AICC_STT_ROLE,
  AiccSttOption,
  AiccSttRole,
  AiccSttSettingPayload,
  AiccSttSettingRow,
} from "types/model/aiccLearningAssets";
import AvailableOptions from "./AvailableOptions";
import getColumns from "./columnConfig";
import { STT_ACTIVE_CONFIGURATION_TITLE, STT_FIELD_GUIDE_INTRO } from "./configs";
import LearningAssetPage from "../LearningAssetPage";
import learningAssetStyles from "../LearningAssets.module.scss";
import { useLearningAssetVersions } from "../hooks/useLearningAssetVersions";
import { useAiccSttModels } from "../../hooks/useAiccSttModels";

const getRowOptionKey = (rows: AiccSttSettingRow[], role: AiccSttRole) => find(rows, { id: role })?.optionKey;

/**
 * The other slot's engine after one slot changes: keep it when it still shares the new mode, otherwise pick
 * something in that mode. The backend DROPS a cross-mode fallback, so a mismatched pair would advertise
 * resilience that never fires.
 *
 * When it has to pick, it picks a CURRENT engine first: what the screen chooses on the operator's behalf
 * should not be an engine on its way out. Only if the mode has nothing else does it land on a deprecated
 * one — that still beats leaving the slot empty, which the backend would resolve to its own default.
 *
 * Takes the catalogue rather than reading a constant: the modes it pairs on are the ones the agent builds.
 */
const getPairedKey = (options: AiccSttOption[], key: string, otherKey?: string) => {
  const { mode } = find(options, { key }) ?? {};
  const isOtherUsable = otherKey !== key && find(options, { key: otherKey })?.mode === mode;
  if (isOtherUsable) {
    return otherKey;
  }
  const candidates = filter(options, (option) => option.mode === mode && option.key !== key);
  return (find(candidates, (option) => !option.isDeprecated) ?? head(candidates))?.key;
};

function SttSetting() {
  const state = useLearningAssetVersions(aiccSttSettingApi.fetchVersions);
  // The engines the AGENT can build, with their modes and their defaults. This screen
  // used to carry that list itself; `configs.tsx` explains what that cost.
  const { options, isError: isCatalogueError } = useAiccSttModels();
  const { items, languageId, groupStatus, isEditable, isSaving, runWrite } = state;
  const [draft, setDraft] = useState<AiccSttSettingPayload>();

  const isEditing = !isNil(draft);
  const rows = useMemo(
    () =>
      isNil(draft)
        ? items
        : map(items, (row) => ({
            ...row,
            optionKey: row.id === AICC_STT_ROLE.primary ? draft.primaryKey : draft.fallbackKey,
          })),
    [draft, items]
  );

  const startEdit = useCallback(
    () =>
      setDraft({
        primaryKey: getRowOptionKey(items, AICC_STT_ROLE.primary),
        fallbackKey: getRowOptionKey(items, AICC_STT_ROLE.fallback),
      }),
    [items]
  );

  const cancelEdit = useCallback(() => setDraft(undefined), []);

  useEffect(() => {
    cancelEdit();
  }, [cancelEdit, groupStatus, languageId]);

  const saveEdit = useCallback(() => {
    if (isNil(draft)) {
      return;
    }
    runWrite(
      (id) => aiccSttSettingApi.update(id, draft),
      `Updated ${STT_ACTIVE_CONFIGURATION_TITLE} successfully.`,
      cancelEdit
    );
  }, [cancelEdit, draft, runWrite]);

  const changeOption = useCallback(
    (role: AiccSttRole, optionKey: string) => {
      setDraft((current) => {
        if (isNil(current)) {
          return current;
        }
        const isPrimary = role === AICC_STT_ROLE.primary;
        const pairedKey = getPairedKey(options, optionKey, isPrimary ? current.fallbackKey : current.primaryKey);
        return isPrimary
          ? { primaryKey: optionKey, fallbackKey: pairedKey }
          : { primaryKey: pairedKey, fallbackKey: optionKey };
      });
    },
    [options]
  );

  const optionSelectOptions = useMemo(
    () => map(options, ({ key, displayName }) => ({ label: displayName, value: key })),
    [options]
  );

  const columns = useMemo(() => getColumns({ isEditing, optionSelectOptions, options, onOptionChange: changeOption }), [
    changeOption,
    isEditing,
    options,
    optionSelectOptions,
  ]);

  const isSaveDisabled = isNil(draft?.primaryKey) || isNil(draft?.fallbackKey);

  return (
    <>
      <Prompt when={isEditing || isSaving} message={LEAVE_CONFIRMATION} />
      <LearningAssetPage<AiccSttSettingRow>
        title="STT Setting"
        fieldGuideIntro={STT_FIELD_GUIDE_INTRO}
        columns={columns}
        state={{ ...state, items: rows }}
        isEditing={isEditing}
        actions={
          <div className={learningAssetStyles.tableHeader}>
            <AvailableOptions options={options} isError={isCatalogueError} />
            <div className={learningAssetStyles.sectionHeader}>
              <div className={learningAssetStyles.sectionTitle}>{STT_ACTIVE_CONFIGURATION_TITLE}</div>
              {isEditable &&
                (isEditing ? (
                  <Space size={12}>
                    <Button shape="round" disabled={isSaving} onClick={cancelEdit}>
                      Cancel
                    </Button>
                    <Button type="primary" shape="round" disabled={isSaveDisabled || isSaving} onClick={saveEdit}>
                      Save
                    </Button>
                  </Space>
                ) : (
                  <Button type="primary" shape="round" disabled={isNil(languageId)} onClick={startEdit}>
                    Edit
                  </Button>
                ))}
            </div>
          </div>
        }
      />
    </>
  );
}

export default SttSetting;
